import { useState, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import Toast from "../../Common/Toastify";
import { companyID } from "../../Common/UIComponents";
import { fetchFacilities } from "../../API_Service/Settings_Slice";
import { fetchFacilityAvailability } from "../../API_Service/LabBooking_Slice";

// ─────────────────────────────────────────────────────────────
// The LAB LIST is real (Settings_GetFacilityList) and so is which dates/
// hours are actually available — Get_FacilityAvailability returns every
// approved booking on the selected lab within the visible calendar window,
// which drives: disabling fully-booked calendar days, blocking already-
// booked hours in the single-day drag picker, and defaulting a picked
// day's "From" time to right after its last booked slot.
// ─────────────────────────────────────────────────────────────

// ── Generic helpers ──────────────────────────────────────────
// (dateKeyOf/iconForLab/tempHumidityLabel/isDayFullyBooked/buildBookedByDate
// are exported too — LabStatusGridPopup.js reuses them for the "all labs,
// today" card grid instead of duplicating this logic.)
export function dateKeyOf(d) {
  return d.toISOString().split("T")[0];
}
function shortLabel(d) {
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
}
function daysBetweenInclusive(startKey, endKey) {
  const a = new Date(`${startKey}T00:00:00`);
  const b = new Date(`${endKey}T00:00:00`);
  return Math.round((b - a) / 86400000) + 1;
}
// Best-effort icon per lab based on its name — Facility_Name is the only
// descriptive field the API returns, so match on keywords rather than
// showing the same generic icon for every lab type.
export function iconForLab(name = "") {
  const n = name.toLowerCase();
  if (n.includes("emc") || n.includes("electrical")) return "bolt";
  if (n.includes("mechanical") || n.includes("vibration")) return "precision_manufacturing";
  if (n.includes("optic")) return "visibility";
  if (n.includes("acoustic")) return "graphic_eq";
  if (n.includes("calibrat")) return "tune";
  if (n.includes("environment")) return "eco";
  if (n.includes("material")) return "category";
  if (n.includes("chemical")) return "science";
  if (n.includes("micro")) return "biotech";
  return "science";
}
// Same display convention as the Facility Config page's RangeCell: "—" when
// neither bound is set, otherwise "min – max" with "—" for whichever
// individual bound is missing (not the whole range).
function rangeText(min, max, unit) {
  if (min == null && max == null) return null;
  return `${min ?? "—"}${unit} – ${max ?? "—"}${unit}`;
}
export function tempHumidityLabel(lab) {
  const temp = rangeText(lab.tempMin, lab.tempMax, "°C");
  const humidity = rangeText(lab.humidityMin, lab.humidityMax, "%");
  if (!temp && !humidity) return "—";
  return [temp, humidity].filter(Boolean).join(" · ");
}

// ── Hour-range slider (single-day booking) — drag two handles across the
// lab's full 24-hour day (12:00 AM – 12:00 AM) instead of opening two
// separate dropdowns ──
const DAY_START_MIN = 0; // 12:00 AM
const DAY_END_MIN = 24 * 60; // 12:00 AM (next day)
const DAY_SPAN = DAY_END_MIN - DAY_START_MIN;

function minsToLabel(mins) {
  const wrapped = ((mins % 1440) + 1440) % 1440; // 1440 (end of day) → 0 = 12:00 AM
  let h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  const period = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return m === 0 ? `${h}:00 ${period}` : `${h}:${String(m).padStart(2, "0")} ${period}`;
}

const HOUR_PRESETS = [
  { label: "Morning", from: 8 * 60, to: 12 * 60 },
  { label: "Afternoon", from: 12 * 60, to: 16 * 60 },
  { label: "Evening", from: 16 * 60, to: 20 * 60 },
  { label: "Full Day", from: DAY_START_MIN, to: DAY_END_MIN },
];

// ── Real availability, derived from Get_FacilityAvailability's booked
// slots (grouped per date, clipped to the operating window) ──
// True when a date's booked intervals cover the whole day, so the day
// should be disabled on the calendar / show red on the "all labs" grid.
// A 60-min tolerance at the tail end counts too — real bookings routinely
// end at the last full hour (e.g. 11:00 PM) rather than exactly midnight,
// and that shouldn't read as "still available" when the rest of the day
// is solid.
const FULL_DAY_TOLERANCE_MIN = 60;
export function isDayFullyBooked(intervals) {
  if (!intervals || intervals.length === 0) return false;
  const sorted = [...intervals].sort((a, b) => a.startMin - b.startMin);
  let covered = DAY_START_MIN;
  for (const iv of sorted) {
    const s = Math.max(iv.startMin, DAY_START_MIN);
    const e = Math.min(iv.endMin, DAY_END_MIN);
    if (s > covered) return false; // gap — some free time remains
    covered = Math.max(covered, e);
  }
  return covered >= DAY_END_MIN - FULL_DAY_TOLERANCE_MIN;
}
// Groups raw { start, end } Date pairs (from Get_FacilityAvailability) into
// { "yyyy-MM-dd": [{ startMin, endMin }] }, splitting any slot that spans
// midnight across both dates it actually touches.
export function buildBookedByDate(bookedSlots) {
  const map = {};
  bookedSlots.forEach((slot) => {
    const cursor = new Date(slot.start.getFullYear(), slot.start.getMonth(), slot.start.getDate());
    const lastDay = new Date(slot.end.getFullYear(), slot.end.getMonth(), slot.end.getDate());
    while (cursor <= lastDay) {
      const key = dateKeyOf(cursor);
      const dayStart = new Date(cursor);
      const dayEnd = new Date(cursor);
      dayEnd.setHours(23, 59, 59, 999);
      const segStart = slot.start > dayStart ? slot.start : dayStart;
      const segEnd = slot.end < dayEnd ? slot.end : dayEnd;
      if (segEnd > segStart) {
        (map[key] ??= []).push({
          startMin: segStart.getHours() * 60 + segStart.getMinutes(),
          endMin: segEnd.getHours() * 60 + segEnd.getMinutes(),
        });
      }
      cursor.setDate(cursor.getDate() + 1);
    }
  });
  return map;
}
// True if [from, to] overlaps any already-booked interval for that date.
function rangeOverlapsBooked(from, to, intervals) {
  return (intervals ?? []).some((iv) => from < iv.endMin && to > iv.startMin);
}
// Nudges a dragged handle's position out of a booked interval it would
// otherwise land inside — snaps to whichever edge is closer, so the drag
// simply can't be released on top of already-booked hours.
function clampOutOfBlocked(mins, intervals) {
  for (const iv of intervals ?? []) {
    if (mins > iv.startMin && mins < iv.endMin) {
      return mins - iv.startMin < iv.endMin - mins ? iv.startMin : iv.endMin;
    }
  }
  return mins;
}
// Default "From" for a freshly picked day — right after its last booked
// slot if one exists and ends before end-of-day, otherwise 9 AM as before.
function defaultFromForDay(intervals) {
  const sorted = [...(intervals ?? [])].sort((a, b) => b.endMin - a.endMin);
  const lastEnd = sorted[0]?.endMin ?? null;
  // A booking today → start right after it ends. Nothing booked → start
  // from the very beginning of the day, not an arbitrary 9 AM default.
  if (lastEnd != null && lastEnd < DAY_END_MIN) {
    return lastEnd;
  }
  return DAY_START_MIN;
}
// Minutes actually booked for one date, clipped to the operating window and
// merged so overlapping/adjacent booked slots aren't double-counted.
function bookedMinutesForDate(intervals) {
  const clipped = (intervals ?? [])
    .map((iv) => ({ s: Math.max(iv.startMin, DAY_START_MIN), e: Math.min(iv.endMin, DAY_END_MIN) }))
    .filter((iv) => iv.e > iv.s)
    .sort((a, b) => a.s - b.s);
  let total = 0;
  let curStart = null;
  let curEnd = null;
  for (const iv of clipped) {
    if (curEnd === null) {
      curStart = iv.s;
      curEnd = iv.e;
    } else if (iv.s <= curEnd) {
      curEnd = Math.max(curEnd, iv.e);
    } else {
      total += curEnd - curStart;
      curStart = iv.s;
      curEnd = iv.e;
    }
  }
  if (curEnd !== null) total += curEnd - curStart;
  return total;
}
// Total free minutes across a date range — every day's operating window
// (DAY_SPAN) minus whatever's already booked that day.
function rangeFreeMinutes(startKey, endKey, bookedByDate) {
  let total = 0;
  const cursor = new Date(`${startKey}T00:00:00`);
  const end = new Date(`${endKey}T00:00:00`);
  while (cursor <= end) {
    const key = dateKeyOf(cursor);
    total += DAY_SPAN - bookedMinutesForDate(bookedByDate[key]);
    cursor.setDate(cursor.getDate() + 1);
  }
  return total;
}
// "2 days" when every day in the range is fully free; otherwise the actual
// free duration as "N day(s) H hr(s)" (a "day" here = one DAY_SPAN, same
// unit the "Full Day" preset uses) so a range with a partially-booked day
// doesn't get credited for hours it doesn't actually have.
function rangeDurationLabel(startKey, endKey, bookedByDate) {
  const dayCount = daysBetweenInclusive(startKey, endKey);
  const freeMins = rangeFreeMinutes(startKey, endKey, bookedByDate);
  if (freeMins >= dayCount * DAY_SPAN) {
    return `${dayCount} day${dayCount > 1 ? "s" : ""}`;
  }
  const days = Math.floor(freeMins / DAY_SPAN);
  const remMins = freeMins - days * DAY_SPAN;
  const hrs = Math.floor(remMins / 60);
  const mins = remMins % 60;
  const parts = [];
  if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);
  if (hrs > 0) parts.push(`${hrs} hr${hrs > 1 ? "s" : ""}`);
  if (parts.length === 0) parts.push(mins > 0 ? `${mins} min` : "0 hrs");
  return `${parts.join(" ")} available`;
}

function HourRangeSlider({ fromMin, toMin, onChange, blocked = [] }) {
  const trackRef = useRef(null);
  const [dragging, setDragging] = useState(null); // "from" | "to" | null
  const fromRef = useRef(fromMin);
  const toRef = useRef(toMin);
  const blockedRef = useRef(blocked);
  useEffect(() => {
    fromRef.current = fromMin;
    toRef.current = toMin;
    blockedRef.current = blocked;
  }, [fromMin, toMin, blocked]);

  const pct = (mins) => ((mins - DAY_START_MIN) / DAY_SPAN) * 100;
  const snap = (raw) => Math.round(raw / 30) * 30;
  const minsFromClientX = (clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return snap(DAY_START_MIN + ratio * DAY_SPAN);
  };

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      // Snap to the grid, then nudge out of any already-booked interval —
      // dragging can never land the handle strictly inside booked hours.
      const mins = clampOutOfBlocked(minsFromClientX(clientX), blockedRef.current);
      if (dragging === "from") {
        onChange(Math.min(mins, toRef.current - 30), toRef.current);
      } else {
        onChange(fromRef.current, Math.max(mins, fromRef.current + 30));
      }
    };
    const up = () => setDragging(null);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging]);

  const hourTicks = [0, 4, 8, 12, 16, 20, 24]; // 12 AM → 12 AM, every 4 hrs
  const Handle = ({ pos, mins, side }) => (
    <div
      className="absolute top-[21px] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center z-10"
      style={{ left: `${pos}%` }}
    >
      <span className="text-[10px] font-black text-[#08757c] mb-1 whitespace-nowrap">
        {minsToLabel(mins)}
      </span>
      <div
        onMouseDown={() => setDragging(side)}
        onTouchStart={() => setDragging(side)}
        className="w-7 h-7 rounded-full bg-white border-[3px] border-[#08757c] cursor-grab active:cursor-grabbing shadow-sm"
      />
    </div>
  );

  return (
    <div>
      <div
        ref={trackRef}
        className="relative h-11 mx-2 select-none touch-none"
      >
        <div className="absolute top-[19px] left-0 right-0 h-[5px] rounded-full bg-[#e5e9e9]" />
        {blocked.map((iv, i) => {
          const s = Math.max(iv.startMin, DAY_START_MIN);
          const e = Math.min(iv.endMin, DAY_END_MIN);
          if (e <= s) return null;
          return (
            <div
              key={i}
              title="Already booked"
              className="absolute top-[19px] h-[5px] rounded-full bg-red-300"
              style={{ left: `${pct(s)}%`, width: `${pct(e) - pct(s)}%` }}
            />
          );
        })}
        <div
          className="absolute top-[19px] h-[5px] rounded-full bg-[#08757c]"
          style={{
            left: `${pct(fromMin)}%`,
            width: `${pct(toMin) - pct(fromMin)}%`,
          }}
        />
        <Handle pos={pct(fromMin)} mins={fromMin} side="from" />
        <Handle pos={pct(toMin)} mins={toMin} side="to" />
      </div>
      <div className="flex justify-between px-1 mt-6">
        {hourTicks.map((h) => (
          <span
            key={h}
            className="text-[9.5px] font-bold text-[#9eabac] tabular-nums"
          >
            {minsToLabel(h * 60)}
          </span>
        ))}
      </div>
      {blocked.length > 0 && (
        <p className="flex items-center gap-1.5 text-[10px] font-semibold text-[#c0392b] mt-2">
          <span className="w-2.5 h-2.5 rounded-full bg-red-300 inline-block" />
          Red = already booked for this day
        </p>
      )}
    </div>
  );
}

// ── Calendar — range-select by clicking a start day then an end day (not
// locked to a whole week/month). A single day (start === end) is treated
// as "book this one day", so the hour picker below only shows then. ──
function buildCalendarWeeks(year, month) {
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first grid
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - startOffset);
  const weeks = [];
  const cursor = new Date(gridStart);
  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      week.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
}

function RangeCalendar({ monthsToShow, range, onDayPick, bookedByDate }) {
  const today = new Date();
  const todayKey = dateKeyOf(today);
  const months = Array.from({ length: monthsToShow }, (_, i) => {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    return {
      year: d.getFullYear(),
      month: d.getMonth(),
      label: d.toLocaleDateString(undefined, { month: "long", year: "numeric" }),
    };
  });

  return (
    <div className="space-y-6">
      {months.map((m) => {
        const weeks = buildCalendarWeeks(m.year, m.month);
        return (
          <div key={`${m.year}-${m.month}`}>
            <p className="text-center text-xs font-black text-[#08757c] uppercase tracking-widest mb-3">
              {m.label}
            </p>
            <div className="max-w-md mx-auto">
              <div className="grid grid-cols-7 gap-1 mb-1">
                {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
                  <div
                    key={d}
                    className="text-center text-[10px] font-bold text-[#9eabac]"
                  >
                    {d}
                  </div>
                ))}
              </div>
              {weeks.map((week, wi) => (
                <div key={wi} className="grid grid-cols-7 gap-1 mb-1">
                  {week.map((date) => {
                    const key = dateKeyOf(date);
                    const inMonth = date.getMonth() === m.month;
                    const isPast = key < todayKey;
                    const isBooked =
                      inMonth && !isPast && isDayFullyBooked(bookedByDate?.[key]);
                    const disabled = !inMonth || isPast || isBooked;
                    const isEndpoint =
                      key === range?.startKey || key === range?.endKey;
                    const inRange =
                      range?.startKey &&
                      range?.endKey &&
                      key >= range.startKey &&
                      key <= range.endKey;
                    return (
                      <button
                        key={key}
                        type="button"
                        disabled={disabled}
                        onClick={() => onDayPick(date, isBooked)}
                        title={
                          !inMonth
                            ? undefined
                            : isBooked
                              ? "Not available"
                              : isPast
                                ? "Past date"
                                : shortLabel(date)
                        }
                        className={`aspect-square rounded-md text-[11px] font-bold flex items-center justify-center border transition-all ${
                          !inMonth
                            ? "opacity-0 pointer-events-none"
                            : isEndpoint
                              ? "bg-[#08757c] border-[#08757c] text-white"
                              : inRange
                                ? "bg-[#08757c]/15 border-[#08757c]/20 text-[#08757c]"
                                : isBooked
                                  ? "bg-[#eef1f1] border-[#eef1f1] text-[#c3cccc] cursor-not-allowed"
                                  : isPast
                                    ? "bg-transparent border-transparent text-[#dfe4e4] cursor-not-allowed"
                                    : "bg-white border-[#08757c]/50 text-[#08757c] hover:bg-[#e8f4f5]"
                        }`}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function LabAvailabilityPopup({
  locationName,
  initialFacilityId,
  onClose,
  onBook,
}) {
  const dispatch = useDispatch();
  const [labs, setLabs] = useState([]);
  const [labsLoading, setLabsLoading] = useState(true);
  const [labsError, setLabsError] = useState(false);

  const [labIdx, setLabIdx] = useState(0);
  const [labSearch, setLabSearch] = useState("");

  // Calendar — click a start day, then an end day. A single day (start ===
  // end) also gets an hour range (drag, or a preset); a multi-day range
  // books full days throughout, no hour picker.
  const [dateRange, setDateRange] = useState(null); // { startKey, endKey, startLabel, endLabel }
  const [dayFromMin, setDayFromMin] = useState(9 * 60);
  const [dayToMin, setDayToMin] = useState(17 * 60);

  // Real lab list — Settings_GetFacilityList, same source the Application
  // Config → Facility screen reads (Facility_Id / Facility_Name), filtered
  // to active rows.
  useEffect(() => {
    setLabsLoading(true);
    setLabsError(false);
    dispatch(fetchFacilities({ ID: 0, companyID }))
      .then((res) => {
        const rows = res?.payload?.data;
        if (!res?.payload?.success || !Array.isArray(rows)) {
          setLabsError(true);
          return;
        }
        const mapped = rows
          .filter((r) => (r.Status ?? "AC") === "AC")
          .map((r) => ({
            id: String(r.Facility_Id),
            name: r.Facility_Name ?? `Facility ${r.Facility_Id}`,
            tempMin: r.TemperatureMin ?? null,
            tempMax: r.TemperatureMax ?? null,
            humidityMin: r.HumidityMin ?? null,
            humidityMax: r.HumidityMax ?? null,
          }));
        if (mapped.length === 0) {
          setLabsError(true);
          return;
        }
        setLabs(mapped);
        if (initialFacilityId) {
          const idx = mapped.findIndex((l) => l.id === String(initialFacilityId));
          if (idx >= 0) setLabIdx(idx);
        }
      })
      .catch(() => setLabsError(true))
      .finally(() => setLabsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const selectedLab = labs[labIdx] ?? null;

  // Real availability for the selected lab — approved bookings within the
  // visible 2-month calendar window, keyed by date for quick lookup.
  const [bookedSlots, setBookedSlots] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  useEffect(() => {
    if (!selectedLab) return;
    setAvailabilityLoading(true);
    const today = new Date();
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    const to = new Date(today.getFullYear(), today.getMonth() + 2, 0); // end of the 2nd visible month
    dispatch(
      fetchFacilityAvailability({
        facilityID: selectedLab.id,
        companyID,
        fromDate: dateKeyOf(from),
        toDate: dateKeyOf(to),
      }),
    )
      .then((res) => {
        const rows = res?.payload?.data;
        setBookedSlots(
          Array.isArray(rows)
            ? rows
                .filter((r) => r.plannedStartDateHrs && r.plannedEndDateHrs)
                .map((r) => ({
                  start: new Date(r.plannedStartDateHrs),
                  end: new Date(r.plannedEndDateHrs),
                }))
            : [],
        );
      })
      .catch(() => setBookedSlots([]))
      .finally(() => setAvailabilityLoading(false));
  }, [selectedLab, dispatch]);

  // Group into { "yyyy-MM-dd": [{ startMin, endMin }] }, clipping each slot
  // to the calendar day(s) it actually touches so a booking that happens to
  // span midnight still shows up correctly on both dates.
  const bookedByDate = useMemo(() => buildBookedByDate(bookedSlots), [bookedSlots]);

  const resetAllSelections = () => {
    setDateRange(null);
    setDayFromMin(9 * 60);
    setDayToMin(17 * 60);
  };
  const switchLab = (i) => {
    setLabIdx(i);
    resetAllSelections();
  };
  const filteredLabs = useMemo(() => {
    const q = labSearch.trim().toLowerCase();
    if (!q) return labs;
    return labs.filter(
      (l) => l.name.toLowerCase().includes(q) || l.id.toLowerCase().includes(q),
    );
  }, [labs, labSearch]);

  const pickDate = (date, isBooked) => {
    if (isBooked) return;
    const key = dateKeyOf(date);
    const label = shortLabel(date);
    const startingFresh = !dateRange?.startKey || (dateRange.startKey && dateRange.endKey);
    if (startingFresh) {
      // Starting a fresh range — default "From" to right after this day's
      // last booked slot (if it has one), same as before otherwise; "To"
      // keeps the usual 8-hour default span, clamped to end-of-day.
      const from = defaultFromForDay(bookedByDate[key]);
      setDayFromMin(from);
      setDayToMin(Math.min(from + 8 * 60, DAY_END_MIN));
      setDateRange({ startKey: key, endKey: null, startLabel: label, endLabel: null });
    } else if (key < dateRange.startKey) {
      setDateRange({ startKey: key, endKey: dateRange.startKey, startLabel: label, endLabel: dateRange.startLabel });
    } else {
      setDateRange({ ...dateRange, endKey: key, endLabel: label });
    }
  };

  // A single click (just startKey, no endKey yet) already counts as "one
  // day picked" — same as clicking that day twice — so the hour picker
  // shows and it's confirmable right away, no second click required.
  // Only once a SECOND, different day is clicked does it become a range.
  const isSingleDay =
    !!dateRange?.startKey &&
    (!dateRange?.endKey || dateRange.endKey === dateRange.startKey);
  const isFullDayRange = dayFromMin === DAY_START_MIN && dayToMin === DAY_END_MIN;

  // Booked intervals for whichever day is currently picked (single-day
  // mode only) — drives the red zones + drag-clamping in HourRangeSlider.
  const blockedForSelectedDay =
    isSingleDay && dateRange?.startKey ? (bookedByDate[dateRange.startKey] ?? []) : [];

  const canConfirm =
    !!dateRange?.startKey &&
    !(isSingleDay && rangeOverlapsBooked(dayFromMin, dayToMin, blockedForSelectedDay));

  const summaryLabel = (() => {
    if (!dateRange?.startKey) return null;
    if (isSingleDay) {
      return isFullDayRange
        ? `${dateRange.startLabel} · Full day`
        : `${dateRange.startLabel} · ${minsToLabel(dayFromMin)} – ${minsToLabel(dayToMin)}`;
    }
    const durationLabel = rangeDurationLabel(dateRange.startKey, dateRange.endKey, bookedByDate);
    return `${dateRange.startLabel} – ${dateRange.endLabel} (${durationLabel})`;
  })();

  const handleConfirm = () => {
    if (!canConfirm) return;
    Toast(`${selectedLab.name} booked — ${summaryLabel}`, "success");
    onBook?.({
      lab: selectedLab,
      ...dateRange,
      ...(isSingleDay && { dayFromMin, dayToMin, isFullDayRange }),
    });
    onClose?.();
  };

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        background: "rgba(15,23,23,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      {/* Thin, modern scrollbar for the lab picker's results list — the
          default OS scrollbar (wide track + up/down arrow buttons) is what
          reads as dated; Firefox is covered via the inline scrollbarWidth
          style above, this covers Chrome/Edge/Safari. */}
      <style>{`
        .lab-picker-scroll::-webkit-scrollbar { width: 6px; }
        .lab-picker-scroll::-webkit-scrollbar-track { background: transparent; }
        .lab-picker-scroll::-webkit-scrollbar-thumb { background: #c7d1d1; border-radius: 999px; }
        .lab-picker-scroll::-webkit-scrollbar-thumb:hover { background: #9eabac; }
      `}</style>
      <div
        style={{
          position: "relative",
          background: "#fff",
          borderRadius: 20,
          width: "100%",
          maxWidth: 1080,
          maxHeight: "calc(100vh - 40px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e5e9e9] flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-[#3e494a] hover:bg-[#f1f4f4] flex-shrink-0"
            >
              <span className="material-symbols-outlined text-[20px]">
                arrow_back
              </span>
            </button>
            <div className="min-w-0">
              <h3 className="text-base font-extrabold text-[#181c1d] truncate">
                Lab Availability
                {locationName ? ` — ${locationName}` : ""}
              </h3>
              <p className="text-xs text-[#6e797a] truncate">
                Pick a lab, then dates on the calendar · Preview with sample
                data
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {summaryLabel && (
              <span className="hidden sm:flex items-center gap-1.5 text-xs font-bold text-[#08757c] bg-[#e8f4f5] border border-[#08757c]/30 rounded-full px-3 py-1.5">
                <span className="material-symbols-outlined text-[14px]">
                  schedule
                </span>
                {summaryLabel}
              </span>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-[#3e494a] hover:bg-[#f1f4f4]"
            >
              <span className="material-symbols-outlined text-[20px]">
                close
              </span>
            </button>
          </div>
        </div>

        {labsLoading ? (
          <div className="flex-1 flex items-center justify-center gap-3 text-[#6e797a] text-sm">
            <span className="material-symbols-outlined animate-spin text-[#08757c]">
              progress_activity
            </span>
            Loading labs…
          </div>
        ) : labsError || labs.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2 text-center px-6">
            <span className="material-symbols-outlined text-[28px] text-[#ba1a1a]">
              error
            </span>
            <p className="text-sm font-semibold text-[#181c1d]">
              Couldn't load labs
            </p>
            <p className="text-xs text-[#9eabac] max-w-xs">
              Settings_GetFacilityList returned no active facilities for this
              company.
            </p>
          </div>
        ) : (
        <div className="flex flex-1 min-h-0">
          {/* Lab sidebar — always visible list, not a dropdown/menu, so
              browsing many labs is just scrolling, no open/close mechanics. */}
          <div className="w-60 flex-shrink-0 border-r border-[#e5e9e9] flex flex-col bg-[#fbfcfc]">
            <div className="p-3 border-b border-[#e5e9e9] flex-shrink-0">
              <div className="flex items-center gap-2 bg-[#f1f4f4] rounded-lg px-3 py-2">
                <span className="material-symbols-outlined text-[16px] text-[#9eabac]">
                  search
                </span>
                <input
                  value={labSearch}
                  onChange={(e) => setLabSearch(e.target.value)}
                  placeholder="Search labs…"
                  className="bg-transparent text-[13px] flex-1 outline-none placeholder:text-[#9eabac] text-[#181c1d]"
                />
              </div>
              <p className="text-[10px] text-[#9eabac] font-semibold mt-2 px-0.5">
                {labs.length} lab{labs.length !== 1 ? "s" : ""}
              </p>
            </div>
            <div
              className="lab-picker-scroll flex-1 overflow-y-auto px-2 py-2"
              style={{ scrollbarWidth: "thin", scrollbarColor: "#c7d1d1 transparent" }}
            >
              {filteredLabs.length === 0 && (
                <p className="text-center text-[12px] text-[#9eabac] py-8 px-3">
                  No labs match "{labSearch}"
                </p>
              )}
              {filteredLabs.map((lab) => {
                const isCurrent = lab.id === selectedLab?.id;
                return (
                  <button
                    key={lab.id}
                    type="button"
                    onClick={() =>
                      switchLab(labs.findIndex((l) => l.id === lab.id))
                    }
                    className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-all mb-1 ${
                      isCurrent
                        ? "bg-[#08757c] text-white"
                        : "hover:bg-[#f1f4f4] text-[#181c1d]"
                    }`}
                  >
                    <span
                      className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${
                        isCurrent
                          ? "bg-white/20 text-white"
                          : "bg-[#e8f4f5] text-[#08757c]"
                      }`}
                    >
                      <span className="material-symbols-outlined text-[14px]">
                        {iconForLab(lab.name)}
                      </span>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[12px] font-bold leading-tight truncate">
                        {lab.name}
                      </span>
                      <span
                        className={`block text-[9.5px] font-semibold truncate ${isCurrent ? "text-white/70" : "text-[#9eabac]"}`}
                      >
                        {tempHumidityLabel(lab)}
                      </span>
                    </span>
                    {isCurrent && (
                      <span className="material-symbols-outlined text-[15px] ml-auto flex-shrink-0">
                        check
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main column */}
          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex-1 overflow-auto px-6 py-6">
              <div className="text-center mb-4">
                <p className="text-xs font-black text-[#08757c] uppercase tracking-widest">
                  Choose Dates
                </p>
                <p className="text-[11px] text-[#9eabac] mt-0.5">
                  Click a start day, then an end day on {selectedLab.name} —
                  pick just one day to also set specific hours, or a range
                  for multiple full days
                </p>
              </div>
              <div className="relative">
                <RangeCalendar
                  monthsToShow={2}
                  range={dateRange}
                  onDayPick={pickDate}
                  bookedByDate={bookedByDate}
                />
                {/* Covers the calendar specifically (not the whole popup)
                    while that lab's real booked-slot data is still in
                    flight — without this, every day briefly renders as
                    available before the actual availability arrives. */}
                {availabilityLoading && (
                  <div className="absolute inset-0 bg-white/75 backdrop-blur-[1px] flex items-center justify-center gap-2 text-[#08757c] text-xs font-semibold rounded-xl">
                    <span className="material-symbols-outlined animate-spin text-[18px]">
                      progress_activity
                    </span>
                    Loading availability…
                  </div>
                )}
              </div>
            </div>

            {/* Select Hours — pinned right below the calendar's own scroll
                area (not inside it), so once a single day is picked the
                drag slider is immediately reachable without scrolling
                past two months of calendar to find it. */}
            {isSingleDay && (
              <div className="flex-shrink-0 border-t border-[#e5e9e9] px-6 py-4">
                <div className="max-w-md mx-auto">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-black text-[#08757c] uppercase tracking-widest">
                      Select Hours
                    </p>
                    <span className="text-[12px] font-black text-white bg-[#08757c] rounded-full px-3.5 py-1.5 tabular-nums whitespace-nowrap">
                      {isFullDayRange
                        ? "Full Day"
                        : `${minsToLabel(dayFromMin)} – ${minsToLabel(dayToMin)}`}
                    </span>
                  </div>
                  <div className="bg-[#f7fafa] border border-[#e5e9e9] rounded-2xl px-4 pt-3.5 pb-3">
                    <HourRangeSlider
                      fromMin={dayFromMin}
                      toMin={dayToMin}
                      blocked={blockedForSelectedDay}
                      onChange={(f, t) => {
                        setDayFromMin(f);
                        setDayToMin(t);
                      }}
                    />
                  </div>
                  <div className="flex gap-2 mt-3">
                    {HOUR_PRESETS.map((p) => {
                      const isActive = dayFromMin === p.from && dayToMin === p.to;
                      const isBlocked = rangeOverlapsBooked(p.from, p.to, blockedForSelectedDay);
                      return (
                        <button
                          key={p.label}
                          type="button"
                          disabled={isBlocked}
                          title={isBlocked ? "Overlaps an already-booked slot" : undefined}
                          onClick={() => {
                            setDayFromMin(p.from);
                            setDayToMin(p.to);
                          }}
                          className={`flex-1 text-[11px] font-bold py-2 rounded-xl border transition-all ${
                            isBlocked
                              ? "bg-[#f7fafa] border-[#e5e9e9] text-[#c3cccc] cursor-not-allowed"
                              : isActive
                                ? "bg-[#08757c] border-[#08757c] text-white"
                                : "bg-white border-[#e5e9e9] text-[#3e494a] hover:border-[#08757c]"
                          }`}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Footer — legend + confirm */}
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-[#e5e9e9] bg-[#f7fafa] flex-shrink-0 flex-wrap">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="flex items-center gap-1.5 text-[11px] font-semibold text-[#3e494a]">
              <span className="w-3.5 h-3.5 rounded-[4px] border border-[#08757c]/50 bg-white inline-block" />
              Available
            </span>
            <span className="flex items-center gap-1.5 text-[11px] font-semibold text-[#3e494a]">
              <span className="w-3.5 h-3.5 rounded-[4px] bg-[#eef1f1] inline-block" />
              Not Available
            </span>
            <span className="flex items-center gap-1.5 text-[11px] font-semibold text-[#3e494a]">
              <span className="w-3.5 h-3.5 rounded-[4px] bg-[#08757c] inline-block" />
              Selected
            </span>
          </div>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-bold bg-gradient-to-br from-[#08757c] to-[#005b60] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <span className="material-symbols-outlined text-[17px]">
              event_available
            </span>
            {summaryLabel
              ? `Book ${selectedLab?.name} · ${summaryLabel}`
              : "Select a slot to continue"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
